import moment from "moment";
import { SubscriptionStatus } from "../../../OrderFlow/utils/orderFlowUtils";
import { LogLevel } from "../../../Shared/hooks/useLogging/Logging-types";
import { DetailTexts, OrderDetailsText, OrderType, } from "../../Orders/OrderDetailsAccordion/utils/OrderDetailsAccordion-types";
import { set_cancel_reasons, set_custom_text, set_customer_data, set_subscriptions, update_subscription_status, } from "../../constants";
import { SubscriptionsTexts } from "./constsAndEnums";
import { Tokens } from "../../../Shared/sharedTypes/Tokens";
export const onFetchSuccess = (setIsErrorState, setIsLoading, setErrorMessage, dispatch) => (res) => {
    if (!res.data.success) {
        setIsErrorState(true);
        setIsLoading(false);
        setErrorMessage(SubscriptionsTexts.SOMETHING_UNEXPECTED_S1);
        return;
    }
    // orderInfo.Balance is used as sent by the server (readers guard with optional chaining).
    dispatch({ type: set_subscriptions, payload: res.data.subscriptionOrderInfo });
    dispatch({
        type: set_customer_data,
        payload: { address: res.data.customerAddress, billing: res.data.customerBilling },
    });
    dispatch({ type: set_cancel_reasons, payload: res.data.cancelSubscriptionReasons });
    dispatch({
        type: set_custom_text,
        payload: {
            subscriptionPageSubHeading: res.data.subscriptionPageSubHeading,
            subscriptionChangeEffectiveText: res.data.subscriptionChangeEffectiveText,
        },
    });
    setIsLoading(false);
};
export const onFetchError = (setIsErrorState, setIsLoading, setErrorMessage, datadogLog) => (e) => {
    console.error(e);
    datadogLog(e.message, LogLevel.ERROR, undefined, e);
    setIsErrorState(true);
    setIsLoading(false);
    setErrorMessage(SubscriptionsTexts.SOMETHING_UNEXPECTED_S2);
    return;
};
/**
 *
 * @param currentOrder
 * @returns
 */
export const getOrderType = (currentOrder) => {
    var _a;
    if (!!((_a = currentOrder.orderInfo) === null || _a === void 0 ? void 0 : _a.subscription_location_name))
        return OrderType.PICKUP;
    return OrderType.DELIVERY;
};
/**
 * Formats the address lines.
 * @param address
 * @returns
 */
const createAddressLine = (address) => {
    const { Address1, Address2, City, State, Zip } = address;
    return `${Address1 !== null && Address1 !== void 0 ? Address1 : ""}, ${!!Address2 ? `${Address2}, ` : ""} ${City !== null && City !== void 0 ? City : ""}, ${State !== null && State !== void 0 ? State : ""} ${Zip !== null && Zip !== void 0 ? Zip : ""}`;
};
/**
 * Helper function to determine and create which delivery or pickup address to use.
 * @param currentOrder
 * @returns
 */
export const getOrderTypeDetails = (currentOrder) => {
    const orderType = getOrderType(currentOrder);
    if (orderType === OrderType.PICKUP) {
        return currentOrder.orderInfo.subscription_location_name;
    }
    if (orderType === OrderType.DELIVERY) {
        const address = currentOrder.orderInfo.subscription_address;
        return createAddressLine(address);
    }
    return DetailTexts.NO_ADDRESS;
};
/**
 *
 * @param subPeriod
 * @returns
 */
export const getFrequencyText = (subPeriod) => {
    const plural = subPeriod !== null && subPeriod > 1;
    return `${SubscriptionsTexts.EVERY} ${plural ? `${subPeriod} ${SubscriptionsTexts.WEEKS}` : `${SubscriptionsTexts.WEEK}`}`;
};
/**
 *
 * @param subscriptionChangeEffectiveText
 * @param isSubscriptionEditable
 * @param subscriptionIsPausedOrActive
 * @param dateChangesTakeEffect
 * @returns
 */
export const getSubUpdatedEffectiveChangeText = (dateChangesTakeEffect, isSubscriptionEditable, subscriptionChangeEffectiveText, subscriptionIsPausedOrActive, firstDeliveryAfterResume, subscriptionStatus, renewOnResume) => {
    if (!subscriptionChangeEffectiveText || (!isSubscriptionEditable && !subscriptionIsPausedOrActive))
        return "";
    if (renewOnResume &&
        subscriptionStatus === SubscriptionStatus.Paused &&
        firstDeliveryAfterResume != null &&
        moment(firstDeliveryAfterResume).isAfter(moment())) {
        return subscriptionChangeEffectiveText.replace(Tokens.Date, `${moment(firstDeliveryAfterResume).utc().format("ddd, MMM Do")}.`);
    }
    if (dateChangesTakeEffect === null || moment(dateChangesTakeEffect).isBefore(moment()))
        return subscriptionChangeEffectiveText.replace(Tokens.Date, SubscriptionsTexts.YOUR_NEXT_ORDER);
    return subscriptionChangeEffectiveText.replace(Tokens.Date, `${moment(dateChangesTakeEffect).utc().format("ddd, MMM Do")}.`);
};
export const getShowContactUsToMakeChanges = (UsesEditSubscriptions, doesEditFlowExist, subscriptionStatus) => UsesEditSubscriptions && !doesEditFlowExist && subscriptionStatus === SubscriptionStatus.Active;
export const getContactUsToMakeChangesText = (UsesEditSubscriptions, doesEditFlowExist, subscriptionStatus) => UsesEditSubscriptions && !doesEditFlowExist && subscriptionStatus === SubscriptionStatus.Active
    ? SubscriptionsTexts.PLEASE_CONTACT_CHANGES
    : "";
export const getShowPauseButton = (UsesPausedSubscriptions, subscriptionStatus) => UsesPausedSubscriptions && subscriptionStatus === SubscriptionStatus.Active;
export const getShowResumeButton = (UsesPausedSubscriptions, subscriptionStatus) => UsesPausedSubscriptions && subscriptionStatus === SubscriptionStatus.Paused;
export const getShowEditButton = (UsesEditSubscriptions, subscriptionStatus) => UsesEditSubscriptions && subscriptionStatus === SubscriptionStatus.Active;
export const getCCText = (customerCreditCard) => {
    if (customerCreditCard === null || customerCreditCard === void 0 ? void 0 : customerCreditCard.CCType)
        return `${customerCreditCard.CCType} ${SubscriptionsTexts.ENDING_IN} ${customerCreditCard.CCLast4}.`;
    return SubscriptionsTexts.NO_CREDIT_CARD;
};
export const getSummaryLocation = (orderTypeDetails, orderType) => ({
    addressLines: orderTypeDetails,
    orderPickupDeliveryType: orderType === OrderType.DELIVERY ? OrderDetailsText.DELIVER_TO : OrderDetailsText.PICKUP_AT,
});
export const subscriptionsReducer = (store, action) => {
    switch (action.type) {
        case set_subscriptions:
            return Object.assign(Object.assign({}, store), { subscriptions: action.payload });
        case set_cancel_reasons:
            return Object.assign(Object.assign({}, store), { cancelReasons: action.payload });
        case set_customer_data:
            return Object.assign(Object.assign({}, store), { customerData: action.payload });
        case set_custom_text:
            return Object.assign(Object.assign({}, store), { customText: action.payload });
        case update_subscription_status:
            let tempSub = store.subscriptions[action.payload.index];
            tempSub.subscriptionInfo.subscriptionStatus = action.payload.status;
            return Object.assign({}, store);
        default:
            return store;
    }
};
